/**
 * CONTRATO de la API REST de la app web (servidor + cliente importan este archivo).
 *
 * Convenciones:
 *  - Base /api, solo JSON. Errores RFC 9457: application/problem+json
 *    { type, title, status, detail?, errors?: [{ path, message }] }.
 *  - 400 validación · 401 sin sesión · 403 rol/CSRF · 404 no existe o es de otra
 *    institución · 409 conflicto · 415 cuerpo no JSON · 429 límite/cuota.
 *  - IDs: uuid para objetos de institución; bigint como string para avisos/capturas.
 *  - Paginación por cursor opaco: ?cursor= → nextCursor.
 *  - Nunca datos personales en la URL: las búsquedas van en el cuerpo del POST y la
 *    SPA solo guarda ?s=<searchId>.
 *
 * Los tipos de resultados de búsqueda (SearchHit, ListingDetail, Facets, ParsedQuery)
 * vienen del núcleo: packages/collector-core/src/search/types.ts. Aquí se reexportan
 * como tipos (el cliente no importa código del núcleo, solo tipos).
 */

import { z } from "zod";

export type {
  ParsedQuery, SearchHit, SearchFilters, ListingDetail, SnapshotView, IdentifierView,
  Facets, SnippetSegment, MatchField, PlateCandidate,
} from "../../../../packages/collector-core/src/search/types.js";
export type { LiveHit, ManualLink } from "../../../../packages/live-search/src/types.js";
export type {
  ListingPhotoMatches, PhotoMatchListing, PhotoMatchPair, PhotoRef,
} from "../../../../packages/collector-core/src/photos/store.js";
export type { PhotoMatchTier } from "../../../../packages/collector-core/src/photos/fingerprint.js";

// ─── Enumeraciones ──────────────────────────────────────────────────────────

export const ROLES = ["user", "admin"] as const;
export const USER_STATUSES = ["invited", "active", "disabled"] as const;
export const INSTITUTION_STATUSES = ["active", "suspended"] as const;

/** Finalidad de la búsqueda (obligatoria; queda en el historial y la auditoría). */
export const PURPOSES = [
  "claim_investigation", "fraud_prevention", "vehicle_recovery", "underwriting", "legal_defense", "other",
] as const;
export const PURPOSE_LABELS: Record<(typeof PURPOSES)[number], string> = {
  claim_investigation: "Investigación de siniestro",
  fraud_prevention: "Prevención de fraude",
  vehicle_recovery: "Recupero de vehículo",
  underwriting: "Suscripción / evaluación de riesgo",
  legal_defense: "Defensa judicial",
  other: "Otra (especificar)",
};

/** Rol de la persona buscada respecto del caso (opcional). */
export const SUBJECT_ROLES = ["insured", "driver", "third_party", "witness", "unknown"] as const;
export const SUBJECT_ROLE_LABELS: Record<(typeof SUBJECT_ROLES)[number], string> = {
  insured: "Asegurado",
  driver: "Conductor",
  third_party: "Tercero",
  witness: "Testigo",
  unknown: "No especificado",
};

// ─── Auth ───────────────────────────────────────────────────────────────────

export const LoginRequest = z.object({
  email: z.string().trim().min(3).max(254),
  password: z.string().min(1).max(1024),
});
export type LoginRequest = z.infer<typeof LoginRequest>;

/** Política de contraseña (NIST 800-63B-4): largo mínimo, sin reglas de composición. */
export const PASSWORD_MIN = 12;
export const NewPassword = z.string().min(PASSWORD_MIN).max(128);

export const ChangePasswordRequest = z.object({
  currentPassword: z.string().min(1).max(1024),
  newPassword: NewPassword,
});
export type ChangePasswordRequest = z.infer<typeof ChangePasswordRequest>;

export const TokenInspectRequest = z.object({ token: z.string().min(20).max(200) });
export const TokenRedeemRequest = z.object({ token: z.string().min(20).max(200), newPassword: NewPassword });
export type TokenRedeemRequest = z.infer<typeof TokenRedeemRequest>;

export interface TokenInspectResponse {
  purpose: "invite" | "password_reset";
  email: string;
  fullName: string;
  expiresAt: string;
}

export interface Me {
  user: { id: string; email: string; fullName: string; role: (typeof ROLES)[number] };
  institution: { id: string; name: string; slug: string; liveSearchEnabled: boolean } | null;
  session: { idleExpiresAt: string; absoluteExpiresAt: string };
}

// ─── Búsqueda ───────────────────────────────────────────────────────────────

export const SearchFiltersSchema = z.object({
  sources: z.array(z.string().max(60)).max(30).optional(),
  make: z.string().max(60).optional(),
  model: z.string().max(60).optional(),
  yearFrom: z.number().int().min(1900).max(2100).optional(),
  yearTo: z.number().int().min(1900).max(2100).optional(),
  region: z.string().max(80).optional(),
  comuna: z.string().max(80).optional(),
  seenFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  seenTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  priceMin: z.number().int().min(0).optional(),
  priceMax: z.number().int().min(0).optional(),
}).strict();

export const SearchContext = z.object({
  purpose: z.enum(PURPOSES),
  purposeDetail: z.string().trim().max(500).optional(),
  caseRef: z.string().trim().max(80).optional(),
  subjectRole: z.enum(SUBJECT_ROLES).optional(),
}).refine((c) => c.purpose !== "other" || (c.purposeDetail ?? "").length >= 3, {
  message: "Especifica la finalidad", path: ["purposeDetail"],
});
export type SearchContext = z.infer<typeof SearchContext>;

const shortText = (n: number) => z.string().trim().max(n).optional();
const ISO_DAY = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Fecha inválida");

/** Búsqueda avanzada por campos (ninguno obligatorio; ver collector-core/search/advanced.ts). */
export const AdvancedSearch = z.object({
  keywords: shortText(120),
  plate: shortText(12),
  vin: shortText(25),
  make: shortText(40),
  model: shortText(40),
  yearFrom: z.number().int().min(1900).max(2100).optional(),
  yearTo: z.number().int().min(1900).max(2100).optional(),
  color: shortText(30),
  name: shortText(80),
  ruts: z.array(z.string().trim().max(14)).max(5).optional(),
  phones: z.array(z.string().trim().max(20)).max(5).optional(),
  emails: z.array(z.email("Correo inválido").max(254)).max(5).optional(),
  region: shortText(60),
  comuna: shortText(60),
  address: shortText(120),
  incidentDate: ISO_DAY.optional(),
  daysBefore: z.number().int().min(0).max(365).optional(),
  daysAfter: z.number().int().min(0).max(365).optional(),
  onlyWindow: z.boolean().optional(),
}).strict();
export type AdvancedSearch = z.infer<typeof AdvancedSearch>;

export const SearchRequest = z.object({
  /** Caja unificada; con `advanced` es solo una etiqueta (el servidor arma la consulta). */
  q: z.string().trim().min(1).max(200),
  advanced: AdvancedSearch.optional(),
  filters: SearchFiltersSchema.optional(),
  context: SearchContext,
  /** Búsqueda en vivo por internet. Default: según la institución. */
  live: z.boolean().optional(),
  /** "Ampliar" la búsqueda en vivo (más consultas). */
  liveExpand: z.boolean().optional(),
  /**
   * "Seguir la pista" desde un resultado en vivo: en internet se busca SOLO esto
   * (el texto exacto del aviso en otros sitios, o dentro de una cuenta o grupo).
   */
  pivot: z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("phrase"), phrase: z.string().trim().min(6).max(200) }).strict(),
    z.object({ kind: z.literal("within"), site: z.string().trim().min(5).max(300), terms: z.string().trim().max(120).optional() }).strict(),
  ]).optional(),
  limit: z.number().int().min(1).max(50).optional(),
});
export type SearchRequest = z.infer<typeof SearchRequest>;

export type LiveStatus = "queued" | "running" | "done" | "partial" | "error" | "disabled";

export interface SearchResponse {
  searchId: string;
  parsed: import("../../../../packages/collector-core/src/search/types.js").ParsedQuery;
  results: import("../../../../packages/collector-core/src/search/types.js").SearchHit[];
  nextCursor: string | null;
  totalEstimate: number | null;
  broad: boolean;
  tookMs: number;
  /** No se encontraron los identificadores: resultados por el resto de los datos. */
  relaxed?: import("../../../../packages/collector-core/src/search/types.js").SearchResult["relaxed"];
  live: {
    status: LiveStatus;
    /** Motivo cuando status='disabled' (institución sin búsqueda en vivo, sin proveedor configurado, cuota…). */
    reason?: string;
    streamUrl: string | null;   // /api/searches/:id/live/stream (SSE)
    pollUrl: string | null;     // /api/searches/:id/live
  };
  /** Consultas manuales sugeridas (sitios con captcha / ClaveÚnica). */
  manualLinks: import("../../../../packages/live-search/src/types.js").ManualLink[];
}

/** GET /api/searches/:id/results?cursor= */
export interface SearchPage {
  results: import("../../../../packages/collector-core/src/search/types.js").SearchHit[];
  nextCursor: string | null;
}

/** GET /api/searches/:id (reabrir una búsqueda del historial). */
export interface SearchRecord {
  id: string;
  createdAt: string;
  queryText: string;
  parsed: import("../../../../packages/collector-core/src/search/types.js").ParsedQuery;
  filters: z.infer<typeof SearchFiltersSchema>;
  purpose: (typeof PURPOSES)[number];
  purposeDetail: string | null;
  caseRef: string | null;
  subjectRole: (typeof SUBJECT_ROLES)[number] | null;
  resultCount: number;
  liveRequested: boolean;
  user: { id: string; fullName: string; email: string };
  /** Consultas manuales sugeridas (se recalculan desde la consulta al reabrir). */
  manualLinks?: import("../../../../packages/live-search/src/types.js").ManualLink[];
}

/** GET /api/searches?scope=mine|institution&cursor= */
export interface SearchHistoryPage {
  items: Omit<SearchRecord, "parsed" | "filters">[];
  nextCursor: string | null;
}

/** Resultado en vivo (persistido en search_web_results). */
export interface WebResult {
  id: string;
  entity: "person" | "plate" | "vehicle" | "email" | "company" | "text" | "vin" | "phone" | "rut" | "phrase" | "within";
  queryId: string;
  provider: string;
  rank: number;
  url: string;
  domain: string;
  title: string;
  snippet: string | null;
  publishedAt: string | null;
  category: string;
  exactMatch: boolean;
  /** Si la URL ya está en el índice propio, el aviso correspondiente. */
  listingId: string | null;
  /** 1 = de la búsqueda (o de la expansión, queryId X…); 2 = de una pista (queryId Y<pista>.n). */
  level?: 1 | 2;
}

/** GET /api/searches/:id/live (sondeo, alternativa al SSE). */
export interface LiveState {
  status: LiveStatus;
  /** stage: 1 identificación · 2 contexto · 3 fuentes específicas · 4 profundización. */
  queries: { id: string; entity: string; text: string; stage?: number; vertical?: "news"; page?: number }[];
  results: WebResult[];
  providers: { provider: string; queryId: string; stage?: number; ok: boolean; ms: number; count: number; cached: boolean; filtered?: number; error?: string }[];
  error: string | null;
  /** Cobertura real de la corrida (null mientras corre o en corridas anteriores a 036). */
  coverage?: LiveCoverage | null;
  /** Cuándo terminó la corrida (las respuestas de caché pueden ser anteriores). */
  finishedAt?: string | null;
  /** Última vez que se pidió «Actualizar» (sin caché). */
  refreshedAt?: string | null;
}

export type LiveIssue = "tiempo_agotado" | "sin_creditos" | "limite_proveedor" | "error_proveedor" | "guardado";

export interface LiveCoverage {
  /** Consultas planificadas que no alcanzaron a ejecutarse. */
  pending: { id: string; stage: number; reason: "tiempo" | "cancelada" | "sin_proveedor" }[];
  /** Llamadas pagadas al buscador y respuestas reutilizadas de la caché. */
  paidCalls: number;
  cachedCalls: number;
  /** Problemas distintos de "sin coincidencias" (para decirlos tal cual). */
  issues: LiveIssue[];
}

/**
 * SSE GET /api/searches/:id/live/stream — eventos:
 *   event: status  data: { status, queries }
 *   event: result  data: WebResult            (id: <WebResult.id> para Last-Event-ID)
 *   event: provider data: { provider, queryId, ok, ms, count, cached, error? }
 *   event: done    data: { status, count }
 * Heartbeat ": ping" cada 15 s. Cierra tras `done` o 90 s.
 */
export type LiveStreamEvent =
  | { event: "status"; data: { status: LiveStatus; queries: LiveState["queries"]; coverage?: LiveCoverage | null } }
  | { event: "result"; data: WebResult }
  | { event: "provider"; data: LiveState["providers"][number] }
  | { event: "done"; data: { status: LiveStatus; count: number } };

// ─── Resumen con IA de la búsqueda ──────────────────────────────────────────

/**
 * POST /api/searches/:id/summary — resumen sobre los resultados (estilo buscador).
 *   ok        resumen del modelo, con citas validadas
 *   fallback  el modelo no respondió o no pasó la validación: cruces automáticos
 *   empty     sin resultados que resumir
 *   pending   la búsqueda en internet sigue corriendo: volver a pedir en unos segundos
 *   disabled  desactivado para la institución o sin modelo configurado (ver reason)
 */
export type SummaryStatus = "ok" | "fallback" | "empty" | "pending" | "disabled";

export interface SummaryEvidence {
  /** A# aviso propio · W# internet · V# VIN · R# registro de transporte · C# cruce automático · G# Google. */
  id: string;
  kind: "aviso" | "web" | "vin" | "registro" | "cruce" | "google";
  label: string;
  /** /avisos/:id (interno) o URL externa; null si no hay enlace. */
  href: string | null;
}

export interface SearchSummary {
  status: SummaryStatus;
  reason?: string;
  resumen: string | null;
  puntos: { texto: string; citas: string[] }[];
  evidence: SummaryEvidence[];
  model: string | null;
  /** true si se generó con la búsqueda en internet aún en curso. */
  livePending: boolean;
  generatedAt: string | null;
  /** Hay widget de sugerencias de Google (GET /api/searches/:id/summary/suggestions). */
  hasSuggestions: boolean;
}

// ─── Análisis profundo (motor de segundo nivel) ─────────────────────────────

export type IntelMatchClass = "exacta" | "homonimo" | "fuera_de_contexto";

/** Dónde aparece un dato del caso: página leída, imagen (OCR, por revisar) o solo el extracto del buscador. */
export interface IntelEvidence {
  kind: "plate" | "vin" | "phone" | "email" | "rut" | "person";
  value: string;
  where: "pagina" | "ocr" | "extracto";
  fragment: string;
  /** "ejemplo": el dato aparece solo en una demostración del sitio (sigue contando). */
  detail?: "completo" | "parcial" | "montada" | "no_montada" | "ejemplo";
  /** Por qué es una demostración («junto al RUT de muestra 12.345.678-5»). */
  note?: string;
}

export interface IntelDocument {
  id: string;
  webResultId: string | null;
  url: string;
  domain: string;
  level: 1 | 2;
  status: "ok" | "skipped" | "duplicate" | "error";
  reason: string | null;
  kind: "html" | "pdf" | null;
  title: string | null;
  publishedAt: string | null;
  score: number | null;
  class: IntelMatchClass | null;
  reasons: { feature: string; points: number; detail: string }[];
  matched: { kind: string; value: string }[];
  /** Fragmento del texto alrededor del primer dato del caso (máx. ~400 caracteres). */
  excerpt: string | null;
  evidence: IntelEvidence[];
  /** Cuándo se leyó la página. */
  fetchedAt: string;
  imagesRead: number;
}

export interface IntelPivot {
  id: string;
  kind: "plate" | "vin" | "phone" | "email" | "rut";
  value: string;
  mode: "auto" | "suggested";
  status: "suggested" | "queued" | "done" | "dismissed" | "error";
  reason: string;
  sourceDomain: string | null;
  resultCount: number | null;
}

export interface IntelView {
  enabled: boolean;
  /** Trabajos pendientes o en curso de esta búsqueda (0 = terminó). */
  pending: number;
  failed: number;
  documents: IntelDocument[];
  pivots: IntelPivot[];
}

// ─── Señales de fraude (grafo de identificadores) ───────────────────────────

/** Arista del grafo: por qué dos avisos están relacionados. */
export interface SharedKey {
  kind: "plate" | "vin" | "phone" | "rut" | "motor" | "name";
  value: string;
}

/** Otro aviso que comparte identificador fuerte o nombre con el aviso base. */
export interface RelatedListing {
  listingId: string;
  source: string;
  title: string;
  make: string | null;
  model: string | null;
  year: number | null;
  price: number | null;
  comuna: string | null;
  region: string | null;
  seenAt: string | null;
  sharedVia: SharedKey[];
}

export interface ClonedPlate {
  plate: string;
  listings: number;
  variants: number;
  vehicles: string[];
  sources: string[];
}

export interface FleetPhone {
  phone: string;
  listings: number;
  platforms: number;
  sources: string[];
  firstSeenAt: string | null;
  lastSeenAt: string | null;
}

export interface ResalePostAuction {
  plate: string;
  auctionedAt: string | null;
  auctionSources: string[];
  listingId: string;
  resoldIn: string;
  resoldAt: string | null;
  price: number | null;
}

/** Resumen de señales que tocan un aviso (para pintarlas como badges en su ficha). */
export interface ListingSignals {
  clonedPlates: ClonedPlate[];
  fleetPhones: FleetPhone[];
  resalePostAuction: ResalePostAuction[];
}

/** GET /api/listings/:id/related — relacionados + señales que tocan el aviso. */
export interface ListingRelatedResponse {
  related: RelatedListing[];
  signals: ListingSignals;
}

// ─── Misma foto y búsqueda inversa ──────────────────────────────────────────

/** Otro aviso con la misma foto (resumen para las tarjetas del caso y el mapa). */
export interface PhotoMatchBrief {
  listingId: string;
  source: string;
  title: string;
  tier: import("../../../../packages/collector-core/src/photos/fingerprint.js").PhotoMatchTier;
  plates: string[];
}

/** GET /api/listings/photo-matches?ids=1,2,3 — por aviso (máx. 60): otros avisos con la misma foto. */
export interface PhotoMatchSummary {
  byListing: Record<string, PhotoMatchBrief[]>;
}

/** POST /api/photos/upload — foto del caso (se procesa en memoria; no se guarda). */
export const PhotoUploadRequest = z.object({
  /** JPEG/PNG/WebP en base64 (el navegador la achica antes de enviarla). */
  image: z.string().min(100).max(1_000_000),
  context: SearchContext,
}).strict();
export type PhotoUploadRequest = z.infer<typeof PhotoUploadRequest>;

export interface PhotoUploadResponse {
  /** Para pedir la búsqueda en internet de esta foto (vence en minutos). */
  uploadId: string;
  /** false = pantallazo o imagen plana: la comparación no es confiable. */
  comparable: boolean;
  matches: import("../../../../packages/collector-core/src/photos/store.js").PhotoMatchListing[];
}

/** POST /api/photos/lens — una foto guardada de un aviso, o la foto subida. */
export const LensRequest = z.union([
  z.object({ snapshotId: z.string().regex(/^[1-9]d{0,18}$/), index: z.number().int().min(0).max(99) }).strict(),
  z.object({ uploadId: z.string().regex(/^[A-Za-z0-9_-]{20,64}$/) }).strict(),
]);
export type LensRequest = z.infer<typeof LensRequest>;

export interface LensMatch {
  link: string;
  domain: string;
  title: string;
  /** Sitio según Google. */
  site: string;
  tier: import("../../../../packages/collector-core/src/photos/fingerprint.js").PhotoMatchTier;
  distance: number;
  /** Miniatura como data: URI (nunca se carga desde Google en el navegador). */
  thumbnail: string | null;
  /** Si la página ya está en el índice propio, el aviso. */
  listingId: string | null;
}

export interface LensResponse {
  /** Resultados que trajo Google (casi todos autos parecidos, que no se muestran). */
  total: number;
  /** Solo la misma foto o una muy parecida. */
  matches: LensMatch[];
  /** Búsquedas de fotos en internet de la institución hoy, y el tope diario. */
  usedToday: number;
  dailyCap: number;
}

/** GET /api/photos/status */
export interface PhotoStatus {
  lens: boolean;
  reason?: string;
  usedToday: number;
  dailyCap: number;
}

// ─── Admin: instituciones ───────────────────────────────────────────────────

const slug = z.string().trim().toLowerCase().regex(/^[a-z0-9][a-z0-9-]{1,39}$/, "Solo minúsculas, números y guiones");

export const InstitutionCreate = z.object({
  name: z.string().trim().min(2).max(120),
  slug,
  rut: z.string().trim().max(20).optional(),
  maxUsers: z.number().int().positive().nullable().optional(),
  monthlySearchQuota: z.number().int().positive().nullable().optional(),
  liveSearchEnabled: z.boolean().optional(),
  aiSummaryEnabled: z.boolean().optional(),
  notes: z.string().max(2000).optional(),
}).strict();
export type InstitutionCreate = z.infer<typeof InstitutionCreate>;

export const InstitutionUpdate = InstitutionCreate.partial().extend({
  status: z.enum(INSTITUTION_STATUSES).optional(),
}).strict();
export type InstitutionUpdate = z.infer<typeof InstitutionUpdate>;

export interface Institution {
  id: string;
  name: string;
  slug: string;
  rut: string | null;
  status: (typeof INSTITUTION_STATUSES)[number];
  maxUsers: number | null;
  monthlySearchQuota: number | null;
  liveSearchEnabled: boolean;
  aiSummaryEnabled: boolean;
  notes: string | null;
  createdAt: string;
  stats: {
    users: number;
    activeUsers: number;
    searchesThisMonth: number;
    searches30d: number;
    lastActivityAt: string | null;
  };
}

// ─── Admin: targets (fuentes sociales a scrapear) ───────────────────────────

/**
 * Categorías de fuente que el PC scraper consume. `grupos_privados` existe para
 * dejar registro, pero NACE apagado y NO se puede activar (Ley 21.459 / 21.719:
 * nada de datos tras login ajeno); el endpoint que sirve targets al PC además
 * fuerza grupos_privados:[] aunque la tabla tenga filas activas.
 */
export const TARGET_CATEGORIAS = [
  "marketplace", "grupos_publicos", "grupos_comercial", "grupos_noticias", "grupos_privados", "ig", "telegram",
] as const;
export type TargetCategoria = (typeof TARGET_CATEGORIAS)[number];

export const TARGET_CATEGORIA_LABELS: Record<TargetCategoria, string> = {
  marketplace: "Marketplace (por ciudad)",
  grupos_publicos: "Grupos públicos",
  grupos_comercial: "Grupos de uso comercial",
  grupos_noticias: "Grupos de noticias / portonazos",
  grupos_privados: "Grupos privados (apagados)",
  ig: "Instagram",
  telegram: "Telegram (canales públicos, sin cuenta)",
};

export const TargetCreate = z.object({
  categoria: z.enum(TARGET_CATEGORIAS),
  valor: z.string().trim().min(1).max(400),
  notas: z.string().trim().max(1000).optional(),
  /** Cuenta que la trabaja; sin ella, la cuenta por defecto de la categoría. */
  accountId: z.uuid().nullable().optional(),
}).strict();
export type TargetCreate = z.infer<typeof TargetCreate>;

export const TARGET_ACCESOS = ["sin_sesion", "con_sesion"] as const;
export type TargetAcceso = (typeof TARGET_ACCESOS)[number];

export const TargetPatch = z.object({
  activo: z.boolean().optional(),
  /** Si el contenido se ve sin iniciar sesión (null = sin probar). */
  acceso: z.enum(TARGET_ACCESOS).nullable().optional(),
  valor: z.string().trim().min(1).max(400).optional(),
  notas: z.string().trim().max(1000).optional(),
  accountId: z.uuid().nullable().optional(),
}).strict();
export type TargetPatch = z.infer<typeof TargetPatch>;

/** Activar o desactivar varias fuentes de una vez (p. ej. podar las que no rinden). */
export const TargetBulk = z.object({
  ids: z.array(z.union([z.uuid(), z.string().regex(/^[1-9][0-9]{0,18}$/)])).min(1).max(2000),
  activo: z.boolean(),
}).strict();
export type TargetBulk = z.infer<typeof TargetBulk>;

/** Estado de rendimiento de una fuente (ver SourceYield). */
export type SourceYieldStatus = "productiva" | "baja" | "sin_nuevos_14d" | "sin_avisos" | "nueva";

/** Rendimiento de una fuente social con los avisos que ya trajo la recolección. */
export interface SourceYield {
  id: string;
  categoria: TargetCategoria;
  valor: string;
  notas: string | null;
  activo: boolean;
  acceso: TargetAcceso | null;
  cuenta: string | null;
  agregadaEl: string;
  /** Avisos registrados desde esta fuente (total, últimos 7 y 30 días). */
  total: number;
  ultimos7: number;
  ultimos30: number;
  /** Último aviso NUEVO y última vez que se vio un aviso de la fuente. */
  ultimoNuevo: string | null;
  ultimoVisto: string | null;
  /** % de sus avisos con patente confirmada (texto u OCR fuerte). */
  pctPatente: number | null;
  estado: SourceYieldStatus;
}

/** Resumen por plataforma (incluye las que no se miden por fuente: Marketplace, remates, noticias). */
export interface PlatformYield {
  plataforma: string;
  total: number;
  ultimos7: number;
  ultimoVisto: string | null;
  pctPatente: number | null;
  /** Corridas del recolector en 7 días, avisos que reportó y bloqueos (login / verificación). */
  corridas7: number;
  itemsOk7: number;
  bloqueos7: number;
  ultimaCorrida: string | null;
}

export interface SourceYieldReport {
  generadoEl: string;
  fuentes: SourceYield[];
  plataformas: PlatformYield[];
}

export interface Target {
  id: string;
  categoria: TargetCategoria;
  valor: string;
  notas: string | null;
  activo: boolean;
  acceso: TargetAcceso | null;
  /** Cuenta elegida en la fuente (null = la por defecto de la categoría). */
  accountId: string | null;
  /** Cuenta que efectivamente la trabaja (elegida o por defecto); null si ninguna sirve. */
  effectiveAccount: { id: string; alias: string; activo: boolean } | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  /** Del catálogo de candidatas (por URL); null si la fuente no vino de ahí. */
  nombre: string | null;
  region: string | null;
  comuna: string | null;
}

// ─── Admin: recolección social (PCs y cuentas) ──────────────────────────────

export const SOCIAL_NETWORKS = ["facebook", "instagram"] as const;
export type SocialNetwork = (typeof SOCIAL_NETWORKS)[number];

/** Categorías que una cuenta puede trabajar (grupos_privados nunca). */
export const ACCOUNT_CATEGORIAS = ["marketplace", "grupos_publicos", "grupos_comercial", "grupos_noticias", "ig"] as const;
export type AccountCategoria = (typeof ACCOUNT_CATEGORIAS)[number];
export const CATEGORIA_NETWORK: Record<AccountCategoria, SocialNetwork> = {
  marketplace: "facebook",
  grupos_publicos: "facebook",
  grupos_comercial: "facebook",
  grupos_noticias: "facebook",
  ig: "instagram",
};

export const ACCOUNT_ESTADOS = ["sin_sesion", "sesion_pendiente", "activa", "login_requerido", "checkpoint", "error"] as const;
export type AccountEstado = (typeof ACCOUNT_ESTADOS)[number];

export const ACCOUNT_ALIAS_RE = /^[a-z0-9][a-z0-9-]{1,47}$/;

export const SocialAccountCreate = z.object({
  alias: z.string().trim().toLowerCase().regex(ACCOUNT_ALIAS_RE, "Use minúsculas, números y guiones (2 a 48 caracteres)"),
  network: z.enum(SOCIAL_NETWORKS),
  agentId: z.uuid().nullable().optional(),
  dailyCap: z.number().int().min(0).max(5000).nullable().optional(),
  itemGapMs: z.number().int().min(1000).max(120_000).nullable().optional(),
  defaultCategorias: z.array(z.enum(ACCOUNT_CATEGORIAS)).max(ACCOUNT_CATEGORIAS.length).optional(),
  notas: z.string().trim().max(1000).optional(),
}).strict();
export type SocialAccountCreate = z.infer<typeof SocialAccountCreate>;

export const SocialAccountPatch = z.object({
  activo: z.boolean().optional(),
  agentId: z.uuid().nullable().optional(),
  dailyCap: z.number().int().min(0).max(5000).nullable().optional(),
  itemGapMs: z.number().int().min(1000).max(120_000).nullable().optional(),
  defaultCategorias: z.array(z.enum(ACCOUNT_CATEGORIAS)).max(ACCOUNT_CATEGORIAS.length).optional(),
  notas: z.string().trim().max(1000).optional(),
}).strict();
export type SocialAccountPatch = z.infer<typeof SocialAccountPatch>;

/** Sesión cifrada en el navegador para el PC (ver shared/seal.ts). */
export const SealedSessionUpload = z.object({
  blob: z.object({
    v: z.literal(1),
    alg: z.literal("RSA-OAEP-256+A256GCM"),
    kid: z.string().regex(/^[a-f0-9]{16}$/),
    ek: z.string().regex(/^[A-Za-z0-9_-]+$/).max(1024),
    iv: z.string().regex(/^[A-Za-z0-9_-]+$/).max(32),
    ct: z.string().regex(/^[A-Za-z0-9_-]+$/).max(700_000),
  }).strict(),
}).strict();
export type SealedSessionUpload = z.infer<typeof SealedSessionUpload>;

export interface SocialAccount {
  id: string;
  alias: string;
  network: SocialNetwork;
  activo: boolean;
  estado: AccountEstado;
  estadoDetalle: string | null;
  estadoAt: string | null;
  /** PC elegido (null = el PC por defecto). */
  agentId: string | null;
  /** PC que efectivamente la trabaja. */
  effectiveAgent: { id: string; name: string; keyId: string | null; publicKey: { kty: "RSA"; n: string; e: string } | null } | null;
  dailyCap: number | null;
  itemGapMs: number | null;
  defaultCategorias: AccountCategoria[];
  sessionPending: boolean;
  sessionPresent: boolean | null;
  usedToday: number | null;
  targets: number;
  notas: string | null;
  createdAt: string;
}

export const AgentCreate = z.object({ name: z.string().trim().min(1).max(80) }).strict();
export type AgentCreate = z.infer<typeof AgentCreate>;

export const AGENT_COMMAND_KINDS = ["run_now", "open_login", "forget_session"] as const;
export type AgentCommandKind = (typeof AGENT_COMMAND_KINDS)[number];

export interface ScraperAgent {
  id: string;
  name: string;
  host: string | null;
  version: string | null;
  keyId: string | null;
  lastSeenAt: string | null;
  online: boolean;
  isDefault: boolean;
  running: { account: string; target: string } | null;
  /** Cuentas trabajando a la vez (una entrada por cuenta). */
  runningAll: { account: string; target: string }[];
  nextCycleAt: string | null;
  revokedAt: string | null;
  createdAt: string;
  pendingCommands: number;
  lastCommand: { kind: AgentCommandKind; createdAt: string; doneAt: string | null; ok: boolean | null; result: string | null } | null;
}

/** Respuesta de registrar un PC: el token se muestra UNA vez. */
export interface AgentEnrollment {
  agent: ScraperAgent;
  token: string;
}

// ─── Admin: usuarios ────────────────────────────────────────────────────────

export const UserCreate = z.object({
  email: z.string().trim().toLowerCase().min(3).max(254).regex(/^[^@\s]+@[^@\s]+\.[^@\s]+$/, "Email inválido"),
  fullName: z.string().trim().min(2).max(120),
  role: z.enum(ROLES),
  /** Obligatorio si role='user'; prohibido si role='admin'. */
  institutionId: z.uuid().optional(),
}).strict().refine((u) => (u.role === "admin") === (u.institutionId === undefined), {
  message: "Un usuario pertenece a una institución; un administrador no", path: ["institutionId"],
});
export type UserCreate = z.infer<typeof UserCreate>;

export const UserUpdate = z.object({
  fullName: z.string().trim().min(2).max(120).optional(),
  status: z.enum(["active", "disabled"]).optional(),
  institutionId: z.uuid().optional(),
}).strict();
export type UserUpdate = z.infer<typeof UserUpdate>;

export interface User {
  id: string;
  email: string;
  fullName: string;
  role: (typeof ROLES)[number];
  status: (typeof USER_STATUSES)[number];
  institution: { id: string; name: string } | null;
  lastLoginAt: string | null;
  lockedUntil: string | null;
  createdAt: string;
}

/** Respuesta de crear usuario / restablecer contraseña: el enlace se muestra UNA vez. */
export interface InviteLink {
  url: string;          // https://…/set-password#t=<token>
  expiresAt: string;
}

// ─── Admin: auditoría, uso y sistema ────────────────────────────────────────

export interface AuditEntry {
  id: string;
  at: string;
  institution: { id: string; name: string } | null;
  actor: { id: string; email: string; fullName: string } | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  searchId: string | null;
  ip: string | null;
  detail: Record<string, unknown>;
}

export interface UsageRow {
  key: string;            // institución, usuario o día según groupBy
  label: string;
  searches: number;
  liveSearches: number;
  listingViews: number;
  activeUsers: number;
  lastActivityAt: string | null;
}

/**
 * Búsquedas de una institución en un mes calendario. Con hallazgo: algún aviso
 * coincidió por un identificador de lo buscado (patente exacta, VIN, RUT, teléfono,
 * correo, motor o nombre). Sin clasificar: búsquedas anteriores a que se registrara.
 */
export interface MonthlyUsageRow {
  key: string;            // id de la institución, o "platform" (administradores)
  label: string;
  searches: number;
  withFinding: number;
  withoutFinding: number;
  unclassified: number;
  liveSearches: number;
  activeUsers: number;
  monthlySearchQuota: number | null;
}

export interface MonthlyUsage {
  month: string;          // AAAA-MM (hora de Chile)
  rows: MonthlyUsageRow[];
}

/** Un PC scraper visto (de scraper_heartbeats). */
export interface ScraperHost {
  host: string;
  scraperVersion: string | null;
  lastSeenAt: string;
  /** Visto dentro de la ventana "en línea" (p.ej. últimos 15 min). */
  online: boolean;
}

/** Última corrida conocida de una fuente (de source_runs). */
export interface ScraperSourceRun {
  source: string;
  host: string | null;
  lastRunAt: string;
  ran: boolean;
  linksFound: number;
  itemsOk: number;
  itemsErr: number;
  blocked: "login" | "checkpoint" | null;
}

/** Salud del scraper del PC: hosts vistos + última corrida por fuente. */
export interface ScraperHealth {
  hosts: ScraperHost[];
  perSource: ScraperSourceRun[];
}

/** GET /api/admin/system — estado de la recolección (para el admin). */
export interface SystemStatus {
  listings: number;
  snapshots: number;
  bySource: { source: string; listings: number; lastSeenAt: string | null; last24h: number }[];
  ocr: { pending: number; error: number; done: number; none: number; noImage: number };
  alerts: { id: string; tipo: string; platform: string | null; message: string; createdAt: string }[];
  liveProviders: { id: string; configured: boolean }[];
  /** Telemetría PC→VPS (source_runs / scraper_heartbeats). */
  scraperHealth: ScraperHealth;
}

/** Página genérica. */
export interface Page<T> { items: T[]; nextCursor: string | null }

/** Error RFC 9457. */
export interface Problem {
  type: string;
  title: string;
  status: number;
  detail?: string;
  errors?: { path: string; message: string }[];
}
